/**
 * Text search shared by the find bar (one document, in the editor) and the server
 * (all documents), so both count and number matches the same way.
 */
import type { Node as PMNode } from "@tiptap/pm/model";

export interface SearchOptions {
  matchCase: boolean;
  wholeWord: boolean;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The pattern for a search, or null for an empty one. Straight and curly quotes match each
 * other (the editor turns typed ' and " into curly ones). Never matches a line break, so a
 * match stays within one paragraph.
 */
export function searchPattern(text: string, opts: SearchOptions): RegExp | null {
  if (!text || /\n/.test(text)) return null;
  let src = [...text]
    .map((c) => (/['‘’]/.test(c) ? "['‘’]" : /["“”]/.test(c) ? '["“”]' : escape(c)))
    .join("");
  if (opts.wholeWord) src = `(?<![\\p{L}\\p{M}\\p{N}])${src}(?![\\p{L}\\p{M}\\p{N}])`;
  return new RegExp(src, opts.matchCase ? "gu" : "giu");
}

/**
 * A document's text for searching: one line per paragraph or heading, with leaf nodes
 * (hard breaks) as a space, exactly as the editor sees each block.
 */
export function searchableText(doc: PMNode): string {
  const lines: string[] = [];
  doc.descendants((node) => {
    if (node.isTextblock) lines.push(node.textBetween(0, node.content.size, undefined, " "));
    return !node.isTextblock;
  });
  return lines.join("\n");
}

export interface Snippet {
  before: string;
  match: string;
  after: string;
  /** Which match in the document this is, from 0: the editor jumps to the same one. */
  index: number;
}

const CONTEXT_BEFORE = 40;
const CONTEXT_AFTER = 70;

/** Text around a match within its paragraph, cut at a space and marked with … where cut. */
function context(text: string, from: number, to: number): Pick<Snippet, "before" | "after"> {
  const lineStart = text.lastIndexOf("\n", from - 1) + 1;
  const lineEnd = (text.indexOf("\n", to) + 1 || text.length + 1) - 1;
  let start = Math.max(lineStart, from - CONTEXT_BEFORE);
  let end = Math.min(lineEnd, to + CONTEXT_AFTER);
  if (start > lineStart) start = Math.min(from, text.indexOf(" ", start) + 1 || from);
  if (end < lineEnd) end = Math.max(to, text.lastIndexOf(" ", end));
  return {
    before: (start > lineStart ? "…" : "") + text.slice(start, from),
    after: text.slice(to, end) + (end < lineEnd ? "…" : ""),
  };
}

/** How many times the pattern occurs, with the first few occurrences in context. */
export function findInText(text: string, pattern: RegExp, maxSnippets: number): { count: number; snippets: Snippet[] } {
  const snippets: Snippet[] = [];
  let count = 0;
  for (const m of text.matchAll(pattern)) {
    if (!m[0].length) continue;
    if (snippets.length < maxSnippets) {
      snippets.push({ ...context(text, m.index, m.index + m[0].length), match: m[0], index: count });
    }
    count++;
  }
  return { count, snippets };
}

/** One document's matches, as returned by GET /api/search. */
export interface SearchResult {
  docId: string;
  title: string;
  folderId: string | null;
  count: number;
  snippets: Snippet[];
}

export interface SearchResponse {
  results: SearchResult[];
  /** More documents matched than are listed. */
  truncated: boolean;
}
